import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import ChartContainer from './components/ChartContainer';
import IndicatorPanel from './components/IndicatorPanel';
import PatternRecognition from './components/PatternRecognition';
import MarketOverview from './components/MarketOverview';
import AnalysisTools from './components/AnalysisTools';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TechnicalAnalysis = () => {
  const [selectedCrypto, setSelectedCrypto] = useState('BTC/USD');
  const [timeframe, setTimeframe] = useState('1h');
  const [indicators, setIndicators] = useState(['sma20', 'rsi']);
  const [ptsWizardParams, setPtsWizardParams] = useState({
    period1: 10,
    period2: 21,
    source: 'hlc3',
    smoothing: 3,
    threshold1: 53,
    threshold2: -53,
    overbought: 70,
    oversold: 30,
    multiplier: 2.5,
    fibonacci1: 0.618,
    fibonacci2: 0.764
  });
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState('patterns');

  useEffect(() => {
    document.title = 'Technical Analysis - CryptoWizard Pro';
  }, []);

  const handleIndicatorToggle = (indicatorId) => {
    setIndicators(prev => 
      prev?.includes(indicatorId)
        ? prev?.filter(id => id !== indicatorId)
        : [...prev, indicatorId]
    );
  };

  const handleParamChange = (key, value) => {
    setPtsWizardParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePatternSelect = (pattern) => {
    setSelectedPattern(pattern);
    console.log('Pattern selected:', pattern);
  };

  const handleToolSelect = (tool) => {
    setSelectedTool(tool);
    console.log('Tool selected:', tool);
  };

  const handleCryptoSelect = (crypto) => {
    setSelectedCrypto(crypto);
  };

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  const rightPanelTabs = [
    { id: 'patterns', label: 'Patterns', icon: 'Eye' },
    { id: 'tools', label: 'Tools', icon: 'Wrench' },
    { id: 'market', label: 'Market', icon: 'Globe' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Main Content */}
      <div className="pt-[60px] pb-[80px] md:pb-0">
        <div className="flex h-[calc(100vh-60px)]">
          {/* Left Sidebar - Indicators */}
          <div className={`transition-all duration-300 ${sidebarCollapsed ? 'w-0' : 'w-80'} overflow-hidden`}>
            <IndicatorPanel
              indicators={indicators}
              onIndicatorToggle={handleIndicatorToggle}
              ptsWizardParams={ptsWizardParams}
              onParamChange={handleParamChange}
            />
          </div>

          {/* Main Chart Area */}
          <div className="flex-1 p-4 space-y-4">
            {/* Chart Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                >
                  <Icon name={sidebarCollapsed ? "PanelLeftOpen" : "PanelLeftClose"} size={16} />
                </Button>
                <h1 className="text-2xl font-bold text-foreground">Technical Analysis</h1>
              </div>

              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm">
                  <Icon name="Settings" size={16} />
                  <span className="hidden sm:inline ml-1">Settings</span>
                </Button>
                <Button variant="outline" size="sm">
                  <Icon name="Download" size={16} />
                  <span className="hidden sm:inline ml-1">Export</span>
                </Button>
                <Button variant="default" size="sm">
                  <Icon name="Save" size={16} />
                  <span className="hidden sm:inline ml-1">Save Layout</span>
                </Button>
              </div>
            </div>

            {/* Chart Container */}
            <ChartContainer
              selectedCrypto={selectedCrypto}
              timeframe={timeframe}
              indicators={indicators}
              onTimeframeChange={handleTimeframeChange}
            />

            {/* Analysis Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="TrendingUp" size={16} className="text-success" />
                  <span className="text-sm font-medium text-foreground">Trend Analysis</span>
                </div>
                <p className="text-lg font-semibold text-success">Bullish</p>
                <p className="text-xs text-muted-foreground">Strong upward momentum detected</p>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Zap" size={16} className="text-warning" />
                  <span className="text-sm font-medium text-foreground">PTS-WIZARD-V3</span>
                </div>
                <p className="text-lg font-semibold text-warning">Signal: BUY</p>
                <p className="text-xs text-muted-foreground">Confidence: 78%</p>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Target" size={16} className="text-accent" />
                  <span className="text-sm font-medium text-foreground">Price Target</span>
                </div>
                <p className="text-lg font-semibold text-accent">$46,250</p>
                <p className="text-xs text-muted-foreground">Next resistance level</p>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Patterns, Tools, Market */}
          <div className="w-80 border-l border-border">
            {/* Tab Navigation */}
            <div className="flex border-b border-border">
              {rightPanelTabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setRightPanelTab(tab?.id)}
                  className={`flex-1 flex items-center justify-center space-x-1 px-3 py-3 text-sm font-medium transition-colors ${
                    rightPanelTab === tab?.id
                      ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="h-full overflow-y-auto">
              {rightPanelTab === 'patterns' && (
                <PatternRecognition onPatternSelect={handlePatternSelect} />
              )}
              {rightPanelTab === 'tools' && (
                <AnalysisTools onToolSelect={handleToolSelect} />
              )}
              {rightPanelTab === 'market' && (
                <MarketOverview
                  selectedCrypto={selectedCrypto}
                  onCryptoSelect={handleCryptoSelect}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Mobile Bottom Panel */}
      <div className="fixed bottom-[80px] left-0 right-0 md:hidden bg-card border-t border-border">
        <div className="flex items-center justify-around py-2">
          {rightPanelTabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setRightPanelTab(tab?.id)}
              className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-md transition-colors ${
                rightPanelTab === tab?.id
                  ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span className="text-xs font-medium">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Status Bar */}
      <div className="fixed bottom-0 left-0 right-0 md:block hidden bg-card border-t border-border px-4 py-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-4">
            <span>Last Update: {new Date()?.toLocaleTimeString()}</span>
            <span>•</span>
            <span>Data Source: Real-time</span>
            <span>•</span>
            <span>Latency: 12ms</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full"></div>
            <span>Connected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalAnalysis;